/**
 * Article Table of Contents
 * - Finds H2 (and optionally H3) within .article-template__content
 * - Generates IDs for headings if missing (slugified, German umlaut aware)
 * - Creates list items with smooth-scroll links
 * - Uses IntersectionObserver to highlight active section
 * - Handles mobile toggle (collapsible)
 *
 * H3 nesting: opt-in via `data-toc-include-h3` attribute on the nav element.
 * When enabled, H3s that appear between two H2s are rendered as a nested list
 * under the preceding H2's list item.
 */
(function () {
  'use strict';

  function slugify(text) {
    return text
      .toLowerCase()
      .replace(/[äÄ]/g, 'ae')
      .replace(/[öÖ]/g, 'oe')
      .replace(/[üÜ]/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function ensureId(heading) {
    if (heading.id) return heading.id;
    let base = slugify(heading.textContent.trim());
    if (!base) base = 'abschnitt';
    let id = base;
    let counter = 1;
    while (document.getElementById(id)) {
      id = base + '-' + counter;
      counter++;
    }
    heading.id = id;
    return id;
  }

  function buildLink(heading, onClickCleanup) {
    const a = document.createElement('a');
    a.href = '#' + heading.id;
    a.textContent = heading.textContent.trim();
    a.addEventListener('click', (e) => {
      e.preventDefault();
      const target = document.getElementById(heading.id);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        history.replaceState(null, '', '#' + heading.id);
      }
      onClickCleanup();
    });
    return a;
  }

  function init() {
    const tocNavs = document.querySelectorAll('[data-section-id]');

    tocNavs.forEach((nav) => {
      const sectionId = nav.dataset.sectionId;
      const list = nav.querySelector('[data-toc-list]');
      if (!list) return;

      const contentEl = document.querySelector('.article-template__content');
      if (!contentEl) return;

      const includeH3 = nav.hasAttribute('data-toc-include-h3');
      const selector = includeH3 ? 'h2, h3' : 'h2';
      const headings = contentEl.querySelectorAll(selector);
      const h2Count = contentEl.querySelectorAll('h2').length;

      if (h2Count === 0) {
        nav.style.display = 'none';
        return;
      }

      // Add layout class to article element for desktop sidebar positioning
      const articleEl = document.querySelector('.article-template');
      if (articleEl) {
        articleEl.classList.add('article-template--has-toc');
      }

      // Close mobile accordion helper (shared by all link clicks)
      const closeMobile = () => {
        const toggle = nav.querySelector('button[aria-expanded]');
        if (toggle && window.innerWidth < 990) {
          toggle.setAttribute('aria-expanded', 'false');
          const wrapper = nav.querySelector('[id^="toc-list-"]');
          if (wrapper) wrapper.classList.remove('is-open');
        }
      };

      // Build TOC items. For nested mode, group H3s under the previous H2.
      const ids = [];
      let currentH2Sublist = null;

      headings.forEach((heading) => {
        const id = ensureId(heading);
        ids.push(id);

        const li = document.createElement('li');
        const a = buildLink(heading, closeMobile);
        li.appendChild(a);

        if (heading.tagName === 'H2') {
          list.appendChild(li);
          currentH2Sublist = null;
        } else if (heading.tagName === 'H3' && includeH3) {
          // Nest under the previous H2's <li>
          const lastH2Li = list.lastElementChild;
          if (!lastH2Li) {
            // H3 before any H2 — skip nesting, append at root
            list.appendChild(li);
            return;
          }
          if (!currentH2Sublist) {
            currentH2Sublist = document.createElement('ol');
            currentH2Sublist.className = 'ratgeber-article__toc-sublist';
            lastH2Li.appendChild(currentH2Sublist);
          }
          li.classList.add('ratgeber-article__toc-subitem');
          currentH2Sublist.appendChild(li);
        }
      });

      // IntersectionObserver for active section highlighting
      const links = list.querySelectorAll('a');
      let activeIndex = 0;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const idx = ids.indexOf(entry.target.id);
              if (idx !== -1) {
                activeIndex = idx;
                links.forEach((link, i) => {
                  link.classList.toggle('active', i === activeIndex);
                });
              }
            }
          });
        },
        {
          rootMargin: '-80px 0px -60% 0px',
          threshold: 0,
        }
      );

      headings.forEach((heading) => observer.observe(heading));

      // Mobile toggle
      const toggle = nav.querySelector('button[aria-expanded]');
      if (toggle) {
        toggle.addEventListener('click', () => {
          const expanded = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', !expanded);
          const wrapper = document.getElementById('toc-list-' + sectionId);
          if (wrapper) {
            wrapper.classList.toggle('is-open', !expanded);
          }
        });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
